import {LocalDate, DateTimeFormatter} from '@js-joda/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import React, {useMemo} from 'react';
import {useCapacityContext} from "../Utils/CapacityContextProvider";
import {Holiday, ProjectSchedule} from '../Utils/EmployeeSchedulesCompiler';
import ScheduleCell from './ScheduleCell';

const useStyles = makeStyles(theme => ({
    totals: {
        backgroundColor: theme.custom.tableFooter,
        '& td': {
            color: theme.palette.text.primary,
            fontWeight: 500,
        },
    },
}));

type Props = {
    projectSchedules : ProjectSchedule[];
    label : string;
    addHolidays ?: boolean;
};

const TotalsRow : React.FC<Props> = ({projectSchedules, label, addHolidays} : Props) => {
    const classes = useStyles();
    const {holidays} = useCapacityContext();
    const totals = useMemo(
        () => calculateTotals(projectSchedules, addHolidays ? holidays ?? [] : []),
        [projectSchedules, addHolidays, holidays]
    );

    return (
        <TableRow className={classes.totals}>
            <TableCell>{label}</TableCell>

            {totals.length === 0 ? (
                <TableCell colSpan={8}/>
            ) : totals.map(total => (
                <ScheduleCell key={total.startOfWeek} schedule={total} header/>
            ))}
        </TableRow>
    );
};

type Total = {
    startOfWeek : string;
    scheduled : number;
    actual ?: number;
};

const usFormat = DateTimeFormatter.ofPattern('MM/dd/yyyy');
const isoFormat = DateTimeFormatter.ofPattern('yyyy-M-d');

const calculateTotals = (projectSchedules : ProjectSchedule[], holidays : Holiday[]) : Total[]  => {
    const totals = new Map<string, Total>();

    return [...projectSchedules.reduce((totals, projectSchedules) => {
        return projectSchedules.weeks.reduce((totals, week) => {
            let current = totals.get(week.startOfWeek);

            if (!current) {
                current = {...week};

                if (holidays.length > 0) {
                    const weekStart = LocalDate.parse(current.startOfWeek, isoFormat);
                    const weekEnd = weekStart.plusDays(6);
                    const thisWeeksHolidays = holidays.filter(h => {
                        if (h.isWeekend) {
                            return false;
                        }
                        const holidayDate = LocalDate.parse(h.HolidayDate, usFormat)
                        return (holidayDate.isAfter(weekStart) && holidayDate.isBefore(weekEnd))
                            || holidayDate.isEqual(weekStart)
                            || holidayDate.isEqual(weekEnd);
                    })
                    current.scheduled += thisWeeksHolidays.length * 8;
                }
                totals.set(week.startOfWeek, current);
                return totals;
            }

            current.scheduled += week.scheduled;

            if (!week.actual) {
                return totals;
            }

            if (!current.actual) {
                current.actual = week.actual;
            } else {
                current.actual += week.actual;
            }

            return totals;
        }, totals);
    }, totals).values()].sort((a, b) => a.startOfWeek.localeCompare(b.startOfWeek));
};

export default TotalsRow;
